/**
 * Formats a duration in seconds to a string in the format "m:ss"
 * @param seconds - The duration in seconds
 * @returns A formatted string in the format "m:ss"
 */
export const formatDuration = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats a phone number for display readability
 * @param phone - The phone number string (may include + prefix)
 * @returns A formatted phone number string for display
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle different phone number lengths
  if (digits.length === 11 && digits.startsWith('1')) {
    // US/Canada number with country code: +1 (XXX) XXX-XXXX
    const area = digits.slice(1, 4);
    const exchange = digits.slice(4, 7);
    const number = digits.slice(7, 11);
    return `+1 (${area}) ${exchange}-${number}`;
  } else if (digits.length === 10) {
    // US/Canada number without country code: (XXX) XXX-XXXX
    const area = digits.slice(0, 3);
    const exchange = digits.slice(3, 6);
    const number = digits.slice(6, 10);
    return `(${area}) ${exchange}-${number}`;
  } else if (digits.length > 10) {
    // International number: +XX XXXX XXXX XXXX
    const countryCode = digits.slice(0, digits.length - 10);
    const remaining = digits.slice(digits.length - 10);
    const formatted = remaining.match(/.{1,4}/g)?.join(' ') || remaining;
    return `+${countryCode} ${formatted}`;
  } else {
    // Short numbers or other formats: just add spaces every 4 digits
    return digits.match(/.{1,4}/g)?.join(' ') || digits;
  }
}; 